/**
 * Articles service API calls.
 * 
 * This module provides functions for article management including
 * creating, reading, updating, and deleting articles.
 */

import apiClient from './api';
import { statsCache } from '../utils/statsCache';

/**
 * Create a new article from URL
 * @param {Object} articleData - Article creation data
 * @param {string} articleData.url - Article URL to save
 * @returns {Promise<Object>} Created article data
 */
export const createArticle = async (articleData) => {
  try {
    const response = await apiClient.post('/api/articles/', articleData);
    // Invalidate stats cache since we added a new article
    statsCache.invalidate();
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create article'
    );
  }
};

/**
 * Create multiple articles from URLs in bulk
 * @param {Object} bulkData - Bulk article creation data
 * @param {string[]} bulkData.urls - Array of article URLs to save (max 50)
 * @returns {Promise<Object>} Bulk creation results with individual status for each URL
 */
export const createArticlesBulk = async (bulkData) => {
  try {
    const response = await apiClient.post('/api/articles/bulk', bulkData);
    // Invalidate stats cache since we added articles
    statsCache.invalidate();
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create articles in bulk'
    );
  }
};


/**
 * Get user's articles with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.per_page - Items per page (default: 50)
 * @param {string} params.search - Search query (optional)
 * @param {string} params.domain - Domain filter (optional)
 * @returns {Promise<Object>} Paginated articles response
 */
export const getArticles = async (params = {}, config = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 50,
      ...(params.search && { search: params.search }),
      ...(params.domain && { domain: params.domain }),
    });
    
    const response = await apiClient.get(`/api/articles/?${queryParams}` , config);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch articles'
    );
  }
};

/**
 * Get a specific article by ID
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Article data
 */
export const getArticle = async (articleId) => {
  try {
    const response = await apiClient.get(`/api/articles/${articleId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch article'
    );
  }
};

/**
 * Update an article's metadata
 * @param {number} articleId - Article ID
 * @param {Object} updateData - Update data
 * @param {string} updateData.title - Updated title (optional)
 * @param {string} updateData.summary - Updated summary (optional)
 * @returns {Promise<Object>} Updated article data
 */
export const updateArticle = async (articleId, updateData) => {
  try {
    const response = await apiClient.put(`/api/articles/${articleId}`, updateData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to update article'
    );
  }
};

/**
 * Delete an article
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteArticle = async (articleId) => {
  try {
    const response = await apiClient.delete(`/api/articles/${articleId}`);
    // Invalidate stats cache since we deleted an article
    statsCache.invalidate();
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete article'
    );
  }
};

/**
 * Regenerate AI summary for an article
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Updated article data with new summary
 */
export const regenerateSummary = async (articleId) => {
  try {
    // Regeneration can take 30-120 seconds for complex articles
    const response = await apiClient.post(
      `/api/articles/${articleId}/regenerate-summary`,
      {},
      { timeout: 120000 } // 2 minute timeout
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to regenerate summary'
    );
  }
};

/**
 * Re-fetch article content from the original URL and regenerate summary
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Updated article data with fresh content and summary
 */
export const refetchContent = async (articleId) => {
  try {
    // Refetch can take 1-5 minutes (scraping + OCR + summarization)
    const response = await apiClient.post(
      `/api/articles/${articleId}/refetch-content`,
      {},
      { timeout: 300000 } // 5 minute timeout
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to refetch article content'
    );
  }
};

/**
 * Get user's article statistics (cached to prevent excessive API calls)
 * @param {boolean} force - Force refresh cache
 * @returns {Promise<Object>} Article statistics
 */
export const getArticleStats = async (force = false) => {
  return statsCache.getStats(async () => {
    try {
      // Get the user's timezone offset in minutes from UTC
      // getTimezoneOffset() returns positive for zones west of UTC (e.g., 420 for UTC-7/PDT)
      // We negate it to get the offset to ADD to UTC to get local time (e.g., -420 means "subtract 7 hours from UTC")
      const timezoneOffsetParam = -new Date().getTimezoneOffset();

      const response = await apiClient.get(`/api/articles/stats/overview?timezone_offset=${timezoneOffsetParam}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || 'Failed to fetch article statistics'
      );
    }
  }, force);
};

/**
 * Mark an article as read
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Updated article data
 */
export const markArticleRead = async (articleId) => {
  try {
    const response = await apiClient.post(`/api/articles/${articleId}/mark-read`);
    // Invalidate stats cache since read/unread counts changed
    statsCache.invalidate();
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to mark article as read'
    );
  }
};

/**
 * Mark an article as unread
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Updated article data
 */
export const markArticleUnread = async (articleId) => {
  try {
    const response = await apiClient.post(`/api/articles/${articleId}/mark-unread`);
    // Invalidate stats cache since read/unread counts changed
    statsCache.invalidate();
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to mark article as unread'
    );
  }
};

/**
 * Get processing status for multiple articles in a batch
 * @param {number[]} articleIds - Array of article IDs (max 50)
 * @returns {Promise<Object>} Batch status response with statuses array
 */
export const getBatchArticleStatus = async (articleIds, config = {}) => {
  try {
    if (!articleIds || articleIds.length === 0) {
      return { statuses: [] };
    }
    
    // Limit to 50 articles per batch request to avoid overwhelming the server
    const limitedIds = articleIds.slice(0, 50);
    
    const response = await apiClient.post(
      '/api/articles/batch/status',
      { article_ids: limitedIds },
      config
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get batch article status'
    );
  }
};

/**
 * Get processing status for a single article (legacy method for compatibility)
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Article processing status
 */
export const getArticleStatus = async (articleId) => {
  try {
    const response = await apiClient.get(`/api/articles/${articleId}/status`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get article status'
    );
  }
};

/**
 * Create or update feedback for an article
 * @param {number} articleId - Article ID
 * @param {number} feedbackValue - Feedback value (-1, 0, 1)
 * @returns {Promise<Object>} Feedback response
 */
export const createArticleFeedback = async (articleId, feedbackValue) => {
  try {
    const response = await apiClient.post(`/api/articles/${articleId}/feedback`, {
      feedback: feedbackValue
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create article feedback'
    );
  }
};

/**
 * Get user's feedback for an article
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Feedback response
 */
export const getArticleFeedback = async (articleId) => {
  try {
    const response = await apiClient.get(`/api/articles/${articleId}/feedback`);
    return response.data;
  } catch (error) {
    // Return null if no feedback exists (404 is expected for articles without feedback)
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error(
      error.response?.data?.detail || 'Failed to get article feedback'
    );
  }
};

/**
 * Get articles related to a specific article using semantic similarity
 * @param {number} articleId - Article ID to find related articles for
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by read status: 'unread' or 'read' (optional)
 * @param {number} params.limit - Maximum number of articles to return (default: 10, max: 20)
 * @returns {Promise<Object>} Related articles response with similarity scores
 */
export const getRelatedArticles = async (articleId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      ...(params.status && { status: params.status }),
      ...(params.limit && { limit: params.limit }),
    });

    const url = `/api/articles/${articleId}/related${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch related articles'
    );
  }
};